import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';
import { bookingService } from '../../../../services/bookingService';
import { getCouponErrorDetails, getErrorMessage } from '../../../../utils/errorHandler';

export const useCoupon = (totals, bookingData, setCouponDiscount, showNotification) => {
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const applyCouponCode = async () => {
    console.log('🎟️ Apply Coupon button clicked!');

    if (!couponCode.trim()) {
      showNotification('Please enter a coupon code', 'error');
      return;
    }

    if (!totals.subtotal || totals.subtotal <= 0) {
      showNotification('Invalid order amount', 'error');
      return;
    }

    if (!bookingData.bikeId) {
      showNotification('Vehicle information is missing', 'error');
      return;
    }

    if (!isAuthenticated) {
      showNotification('Please login to apply coupon', 'error');
      setTimeout(() => {
        navigate('/login', { state: { from: location.pathname } });
      }, 1500);
      return;
    }

    setIsLoading(true);

    try {
      const data = await bookingService.validateCoupon(
        couponCode.trim().toUpperCase(),
        parseInt(bookingData.bikeId, 10),
        parseFloat(totals.subtotal)
      );

      console.log('✅ Coupon validation response:', data);

      if (!data.valid) {
        showNotification(data.message || 'Coupon is not valid', 'error');
        setCouponDiscount(0);
        setAppliedCoupon(null);
        setIsLoading(false);
        return;
      }

      const discountAmount = Math.round(data.couponAmount || 0);

      if (discountAmount <= 0) {
        showNotification('No discount applicable with this coupon', 'error');
        setCouponDiscount(0);
        setAppliedCoupon(null);
        setIsLoading(false);
        return;
      }

      if (discountAmount > totals.subtotal) {
        showNotification('Invalid discount calculation', 'error');
        setCouponDiscount(0);
        setAppliedCoupon(null);
        setIsLoading(false);
        return;
      }

      setCouponDiscount(discountAmount);
      setAppliedCoupon({
        code: couponCode.trim().toUpperCase(),
        discount: discountAmount,
        discountedPrice: data.discountedPrice,
        couponId: data.couponId || 0
      });

      showNotification(
        `✅ Coupon applied! You saved ₹${discountAmount.toLocaleString()}`,
        'success'
      );

      console.log('💰 Discount applied:', {
        originalPrice: totals.subtotal,
        discountedPrice: data.discountedPrice,
        couponAmount: discountAmount
      });

    } catch (error) {
      console.error('❌ Coupon validation failed:', error);

      const errorInfo = getCouponErrorDetails(error);

      if (errorInfo.suggestions && errorInfo.suggestions.length > 0) {
        showNotification(
          errorInfo.message,
          'error',
          { suggestions: errorInfo.suggestions }
        );
      } else {
        showNotification(
          getErrorMessage(error) || 'Failed to apply coupon. Please try again.',
          'error'
        );
      }

      setCouponDiscount(0);
      setAppliedCoupon(null);
    } finally {
      setIsLoading(false);
    }
  };

  const removeCoupon = () => {
    console.log('🗑️ Removing coupon:', appliedCoupon?.code);
    setCouponDiscount(0);
    setCouponCode('');
    setAppliedCoupon(null);
    showNotification('Coupon removed successfully', 'success');
  };

  return {
    couponCode,
    setCouponCode,
    appliedCoupon,
    applyCouponCode,
    removeCoupon,
    isLoading
  };
};
